import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeadingH2, Subheading as SubheadingBase } from "components/misc/Headings.js";
import TeamIllustrationSrc from "images/dashboard-lp.png";
import { ReactComponent as serverIcon } from "feather-icons/dist/icons/server.svg";
import { ReactComponent as zapIcon } from "feather-icons/dist/icons/zap.svg";
import { ReactComponent as terminalIcon } from "feather-icons/dist/icons/terminal.svg";
import { ReactComponent as downloadIcon } from "feather-icons/dist/icons/download.svg";
import Content from "_content/SeoContent.js";

const Container = tw.div`relative -mt-24`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24 items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-6/12 flex-shrink-0 relative md:-mr-12 md:ml-12 mt-12`;
const TextColumn = styled(Column)(props => [
  tw`md:w-6/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);

const Image = styled.img(props => [
  props.imageRounded && tw`rounded`,
  props.imageBorder && tw`border`,
  props.imageShadow && tw`shadow`
]);

const TextContent = tw.div`lg:py-8 text-left`;


const Heading = tw(
  SectionHeadingH2
)`mt-4 font-black text-left leading-tight text-gray-800`;
const Description = tw.p`mt-8 text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Features = tw.div`mx-auto md:mx-0 flex flex-col lg:flex-row max-w-xs lg:max-w-none`;
const Feature = tw.div`mt-10 lg:mt-8 flex items-center md:items-start flex-col md:mr-8 last:mr-0 lg:min-w-1/2 lg:max-w-1/2 md:max-w-full md:min-w-full`;

const FeatureHeadingContainer = tw.div`flex items-center`;
const FeatureIconContainer = styled.div`
  ${tw`mx-auto inline-block border border-primary-500 text-primary-500 text-center rounded p-2 flex-shrink-0`}
  ${props => [
    props.iconRoundedFull && tw`rounded-full`,
    props.iconFilled && tw`border-0 bg-primary-500 text-gray-100`
  ]}
  svg {
    ${tw`w-5 h-5`}
  }
`;
const FeatureHeading = tw.h3`ml-3 font-bold text-xl text-gray-800`;

const FeatureDescription = tw.div`mt-4 text-base text-left text-gray-600 leading-relaxed`;


export default ({
  subheading = "Cutting edge trading automation",
  heading = "Powerful, easy to use trading bot",
  description = "Generate profits by automatically executing your trading strategies. What sets Gunbot apart from other bots are the innovative strategies, speed, extreme flexibility and awesome user community.",
  imageSrc = TeamIllustrationSrc,
  imageRounded = true,
  imageBorder = false,
  imageShadow = false,
  showDecoratorBlob = false,
  textOnLeft = true,
  features = null,
  iconRoundedFull = true,
  iconFilled = true,
  iconContainerCss = null
}) => {
  // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.

  /*
   * Change the features variable as you like, add or delete objects
   * `icon` must be a React SVG component. See how BriefcaseIcon is imported above. For a full list of available icons, see Feather Icons.
   */

  const [urlPath, setUrlPath] = useState("");
  let location = useLocation()

  useEffect(() => {
    let addSlash = !location.pathname.endsWith('/')
    const pathRef = addSlash ? location.pathname + '/' : location.pathname
    if (Object.keys(Content.pages).includes(pathRef)){
      setUrlPath(pathRef)
    }
  }, [location]);

  const block2Heading = Content.pages[urlPath].block2Heading || "";
  const block2Content = Content.pages[urlPath].block2Content || "";
  const block2Alt = Content.pages[urlPath].block1Alt || "";

  const defaultFeatures = [
    {
      Icon: serverIcon,
      title: Content.pages[urlPath].feature1Title || "",
      description: Content.pages[urlPath].feature1Description || "",
      iconContainerCss: tw`bg-teal-300 text-teal-800`
    },
    {
      Icon: zapIcon,
      title: Content.pages[urlPath].feature2Title || "",
      description: Content.pages[urlPath].feature2Description || "",
      iconContainerCss: tw`bg-teal-300 text-teal-800`
    }
  ];

  const defaultFeatures2 = [
    {
      Icon: terminalIcon,
      title: Content.pages[urlPath].feature3Title || "",
      description: Content.pages[urlPath].feature3Description || "",
      iconContainerCss: tw`bg-teal-300 text-teal-800`
    },
    {
      Icon: downloadIcon,
      title: Content.pages[urlPath].feature4Title || "",
      description: Content.pages[urlPath].feature4Description || "",
      iconContainerCss: tw`bg-teal-300 text-teal-800`
    }
  ];


  if (!features) features = defaultFeatures;

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image src={imageSrc} imageBorder={imageBorder} imageShadow={imageShadow} imageRounded={imageRounded} alt={block2Alt} loading="lazy"/>
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            <Heading>{block2Heading}</Heading>
            <Description className="display-linebreak">{block2Content}</Description>
            <Features>
              {features.map((feature, index) => (
                <Feature key={index}>
                  <FeatureHeadingContainer>
                    <FeatureIconContainer
                      iconFilled={iconFilled}
                      iconRoundedFull={iconRoundedFull}
                      css={feature.iconContainerCss || iconContainerCss}
                    >
                      {<feature.Icon />}
                    </FeatureIconContainer>
                    <FeatureHeading>{feature.title}</FeatureHeading>
                  </FeatureHeadingContainer>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </Feature>
              ))}
            </Features>
            <Features>
              {defaultFeatures2.map((feature, index) => (
                <Feature key={index}>
                  <FeatureHeadingContainer>
                    <FeatureIconContainer
                      iconFilled={iconFilled}
                      iconRoundedFull={iconRoundedFull}
                      css={feature.iconContainerCss || iconContainerCss}
                    >
                      {<feature.Icon />}
                    </FeatureIconContainer>
                    <FeatureHeading>{feature.title}</FeatureHeading>
                  </FeatureHeadingContainer>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </Feature>
              ))}
            </Features>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
